import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Send, Loader2, Lock, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import jsPDF from "jspdf";
import { clientSchema } from "@/lib/validation";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface InvoiceActionsProps {
  materials: any[];
  laborCost: number;
  total: number;
  photoUrl: string;
  isAuthenticated: boolean;
  onSignUpRequired: () => void;
}

export const InvoiceActions = ({ 
  materials, 
  laborCost, 
  total, 
  photoUrl, 
  isAuthenticated,
  onSignUpRequired 
}: InvoiceActionsProps) => {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientContact, setClientContact] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<"email" | "telegram">("email");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [businessProfile, setBusinessProfile] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchClients();
      fetchBusinessProfile();
    }
  }, [isAuthenticated]);

  const fetchBusinessProfile = async () => {
    if (!isAuthenticated) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    const { data } = await supabase
      .from("business_profiles")
      .select("*")
      .eq("user_id", user.id)
      .single();
    
    setBusinessProfile(data);
  };

  const fetchClients = async () => {
    if (!isAuthenticated) return;
    const { data } = await supabase.from("clients").select("*").order("name");
    setClients(data || []);
  };

  const handleClientSelect = (clientId: string) => {
    setSelectedClientId(clientId);
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setClientName(client.name);
      setClientContact(client.contact);
      setClientEmail(client.email || "");
      setTelegramChatId(client.telegram_chat_id || "");
      setDeliveryMethod(client.channel_pref);
    }
  };

  const generatePDFDocument = () => {
    const doc = new jsPDF();

    // Set margins and starting position
    const margin = 20;
    let yPosition = margin;
      
      // Add business logo if available
      if (businessProfile?.business_logo_url) {
        try {
          doc.addImage(businessProfile.business_logo_url, 'PNG', margin, yPosition, 40, 40);
          yPosition += 45;
        } catch (error) {
          console.error("Error adding logo:", error);
          yPosition += 10;
        }
      }
      
      // Business Information
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(businessProfile?.business_name || "Your Business", margin, yPosition);
      yPosition += 7;
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      if (businessProfile?.business_address) {
        const addressLines = doc.splitTextToSize(businessProfile.business_address, 80);
        doc.text(addressLines, margin, yPosition);
        yPosition += addressLines.length * 5;
      }
      if (businessProfile?.business_phone) {
        doc.text(`Phone: ${businessProfile.business_phone}`, margin, yPosition);
        yPosition += 5;
      }
      if (businessProfile?.business_email) {
        doc.text(`Email: ${businessProfile.business_email}`, margin, yPosition);
        yPosition += 5;
      }
      
      // Invoice Title and Number
      yPosition += 10;
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("INVOICE", 150, yPosition);
      yPosition += 8;
      
      const invoicePrefix = businessProfile?.invoice_prefix || "INV";
      const invoiceNumber = `${invoicePrefix}-${Date.now()}`;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Invoice #: ${invoiceNumber}`, 150, yPosition);
      yPosition += 5;
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 150, yPosition);
      yPosition += 15;
      
      // Client Information
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Bill To:", margin, yPosition);
      yPosition += 7;
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(clientName || "N/A", margin, yPosition);
      yPosition += 5;
      doc.text(clientContact || "N/A", margin, yPosition);
      yPosition += 5;
      if (clientEmail) {
        doc.text(clientEmail, margin, yPosition);
        yPosition += 5;
      }
      
      yPosition += 10;
      
      // Table Header
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, yPosition, 170, 8, 'F');
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Item Description", margin + 2, yPosition + 5);
      doc.text("Quantity", 130, yPosition + 5);
      doc.text("Amount (RM)", 160, yPosition + 5);
      yPosition += 10;
      
      // Table Content
      doc.setFont("helvetica", "normal");
      materials.forEach((material: any, index: number) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = margin;
        }
        
        const itemText = doc.splitTextToSize(material.name, 120);
        doc.text(itemText, margin + 2, yPosition + 3);
        doc.text(material.quantity.toString(), 135, yPosition + 3);
        doc.text(material.estimated_cost.toFixed(2), 175, yPosition + 3);
        
        const lineHeight = Math.max(itemText.length * 5, 8);
        yPosition += lineHeight;
        
        // Add line separator
        if (index < materials.length - 1) {
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, yPosition, 190, yPosition);
          yPosition += 2;
        }
      });
      
      // Total Section
      yPosition += 10;
      doc.setDrawColor(0, 0, 0);
      doc.line(130, yPosition, 190, yPosition);
      yPosition += 7;
      
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Total:", 130, yPosition);
      doc.text(`RM ${total.toFixed(2)}`, 175, yPosition);
      
      // Footer
      yPosition = 280;
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(128, 128, 128);
      doc.text("Thank you for your business!", 105, yPosition, { align: "center" });
      doc.setTextColor(0, 0, 0);
      
    return doc;
  };

  const previewPDF = () => {
    try {
      const doc = generatePDFDocument();
      const pdfBlob = doc.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (error: any) {
      toast({
        title: "Error generating preview",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      // Get user if authenticated
      const { data: { user } } = isAuthenticated ? await supabase.auth.getUser() : { data: { user: null } };

      const doc = generatePDFDocument();
      const pdfBlob = doc.output("blob");
      
      // If user is authenticated, save to database
      if (isAuthenticated && user) {
        // Upload to storage
        const fileName = `${user.id}/${Date.now()}.pdf`;
        const { error: uploadError } = await supabase.storage
          .from("invoices")
          .upload(fileName, pdfBlob);

        if (uploadError) throw uploadError;

        // Generate signed URL (valid for 1 hour) for private bucket
        const { data: signedUrlData, error: signedUrlError } = await supabase.storage
          .from("invoices")
          .createSignedUrl(fileName, 3600);

        if (signedUrlError) throw signedUrlError;
        const publicUrl = signedUrlData.signedUrl;

        // Save or get client
        let clientId = selectedClientId;
        if (!clientId) {
          const { data: newClient, error: clientError } = await supabase
            .from("clients")
            .insert({
              user_id: user.id,
              name: clientName,
              contact: clientContact,
              email: clientEmail || null,
              telegram_chat_id: telegramChatId || null,
              channel_pref: deliveryMethod,
            })
            .select()
            .single();

          if (clientError) throw clientError;
          clientId = newClient.id;
        }

        // Save to database
        const finalInvoiceNumber = businessProfile?.invoice_prefix 
          ? `${businessProfile.invoice_prefix}-${Date.now()}`
          : `INV-${Date.now()}`;
        const { data: invoice, error: invoiceError } = await supabase
          .from("invoices")
          .insert({
            user_id: user.id,
            client_id: clientId,
            invoice_number: finalInvoiceNumber,
            materials,
            labor_cost: laborCost,
            total,
            photo_url: photoUrl,
            status: "draft",
          })
          .select()
          .single();

        if (invoiceError) throw invoiceError;

        await supabase.from("files").insert({
          invoice_id: invoice.id,
          file_url: publicUrl,
        });

        toast({ title: "Invoice generated!", description: "Ready to send to client." });
        return publicUrl;
      } else {
        // For trial users, just download the PDF directly
        doc.save(`invoice-${Date.now()}.pdf`);
        toast({ 
          title: "Invoice generated!", 
          description: "PDF downloaded. Sign up to save and send invoices." 
        });
        return null;
      }
    } catch (error: any) {
      toast({
        title: "Error generating invoice",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const sendInvoice = async () => {
    // Check authentication first
    if (!isAuthenticated) {
      toast({
        title: "Sign up required",
        description: "Please sign up to send invoices to clients",
        variant: "destructive",
      });
      onSignUpRequired();
      return;
    }

    setSending(true);
    try {
      // Validate client data
      const validation = clientSchema.safeParse({
        name: clientName,
        contact: clientContact,
        email: clientEmail || "",
        telegram_chat_id: telegramChatId || "",
        channel_pref: deliveryMethod,
      });

      if (!validation.success) {
        const errors = validation.error.errors.map(e => e.message).join(", ");
        throw new Error(errors);
      }

      const pdfUrl = await generatePDF();
      if (!pdfUrl) return;

      // Get the invoice ID
      const { data: invoices } = await supabase
        .from("invoices")
        .select("id")
        .eq("photo_url", photoUrl)
        .order("created_at", { ascending: false })
        .limit(1);

      const invoiceId = invoices && invoices.length > 0 ? invoices[0].id : null;

      if (deliveryMethod === "email") {
        // Validate email format before sending
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!clientEmail || !emailRegex.test(clientEmail)) {
          throw new Error("Invalid email address. Please provide a valid email for this client.");
        }

        // Call server-side email function
        const { data, error } = await supabase.functions.invoke("send-email", {
          body: {
            to_email: clientEmail,
            to_name: clientName,
            invoice_url: pdfUrl,
            total: `RM ${total.toFixed(2)}`,
            invoice_id: invoiceId,
          },
        });

        if (error) {
          console.error("Email send error:", error);
          throw new Error(error.message || "Failed to send email");
        }
      } else {
        // Validate Telegram chat ID
        if (!telegramChatId || telegramChatId.trim() === "") {
          throw new Error("Telegram Chat ID is required. Please ask the client to start a chat with your bot and provide their chat ID.");
        }

        // Telegram integration
        const { data, error } = await supabase.functions.invoke("send-telegram", {
          body: {
            chatId: telegramChatId,
            message: `Invoice for ${clientName}\nTotal: RM ${total.toFixed(2)}`,
            pdfUrl,
            invoice_id: invoiceId,
          },
        });

        if (error) {
          console.error("Telegram send error:", error);
          throw new Error(error.message || "Failed to send via Telegram");
        }
      }

      // Update invoice status (using the invoiceId we already have)
      if (invoiceId) {
        await supabase
          .from("invoices")
          .update({ status: "sent" })
          .eq("id", invoiceId);
      }

      toast({
        title: "Invoice sent!",
        description: `Invoice sent via ${deliveryMethod} to ${clientName}`,
      });
    } catch (error: any) {
      toast({
        title: "Error sending invoice",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl h-[90vh]" aria-describedby="invoice-preview-description">
          <DialogHeader>
            <DialogTitle>Invoice Preview</DialogTitle>
            <p id="invoice-preview-description" className="sr-only">
              Preview of the generated invoice before sending
            </p>
          </DialogHeader>
          {previewUrl && (
            <iframe
              src={previewUrl}
              className="w-full h-full rounded-lg"
              title="Invoice Preview"
            />
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Client & Delivery</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
        {!isAuthenticated && (
          <Alert className="border-primary/50 bg-primary/5">
            <Lock className="h-4 w-4" />
            <AlertTitle>Preview Mode</AlertTitle>
            <AlertDescription>
              You can fill in the details below, but you'll need to sign up to generate and send invoices.
            </AlertDescription>
          </Alert>
        )}
        
        {isAuthenticated && clients.length > 0 && (
          <div className="space-y-2">
            <Label htmlFor="client-select">Select Client</Label>
            <Select value={selectedClientId} onValueChange={handleClientSelect}>
              <SelectTrigger id="client-select">
                <SelectValue placeholder="Choose existing client or add new" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} - {client.contact}{client.email ? ` / ${client.email}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="client-name">Client Name</Label>
          <Input
            id="client-name"
            placeholder="John Doe"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="client-contact">Phone Number</Label>
          <Input
            id="client-contact"
            type="tel"
            placeholder="+60123456789"
            value={clientContact}
            onChange={(e) => setClientContact(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="client-email">Email Address</Label>
          <Input
            id="client-email"
            type="email"
            placeholder="client@example.com"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="telegram-chat-id">
            Telegram Chat ID
            <span className="text-xs text-muted-foreground ml-2">
              (Required for Telegram delivery)
            </span>
          </Label>
          <Input
            id="telegram-chat-id"
            type="text"
            placeholder="@username or 123456789"
            value={telegramChatId}
            onChange={(e) => setTelegramChatId(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Ask your client to message your Telegram bot to get their chat ID, or use their @username
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="delivery">Delivery Method</Label>
          <Select value={deliveryMethod} onValueChange={(v: any) => setDeliveryMethod(v)}>
            <SelectTrigger id="delivery">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="telegram">Telegram</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex flex-col gap-2 pt-4">
          <Button
            onClick={previewPDF}
            disabled={!clientName || !clientContact || (deliveryMethod === "email" && !clientEmail) || (deliveryMethod === "telegram" && !telegramChatId)}
            variant="outline"
            className="w-full"
          >
            <Eye className="mr-2 h-4 w-4" />
            Preview Invoice
          </Button>

          {isAuthenticated ? (
            <div className="flex gap-2">
              <Button
                onClick={generatePDF}
                disabled={!clientName || !clientContact || (deliveryMethod === "email" && !clientEmail) || (deliveryMethod === "telegram" && !telegramChatId) || generating}
                variant="outline"
                className="flex-1"
              >
                {generating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <FileText className="mr-2 h-4 w-4" />
                Generate PDF
              </Button>
              
              <Button
                onClick={sendInvoice}
                disabled={!clientName || !clientContact || (deliveryMethod === "email" && !clientEmail) || (deliveryMethod === "telegram" && !telegramChatId) || sending}
                className="flex-1 bg-gradient-accent"
              >
                {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Send className="mr-2 h-4 w-4" />
                Send Invoice
              </Button>
            </div>
          ) : (
            <Button
              onClick={onSignUpRequired}
              className="w-full bg-gradient-accent"
            >
              <Lock className="mr-2 h-4 w-4" />
              Sign Up to Generate & Send Invoices
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
    </>
  );
};
